import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Text } from 'elements';
import { RootState } from 'modules';
import { Moment } from 'moment';
import EditorComponent from 'components/base/EditorComponent';
import { showAlert } from 'components/modal/showConfirm';
import { saveQNA, getQNAItem, getQNACategory, getQNASubcategory } from 'libs/api/case';
import { trim, getUrlQuery, toUpperCase } from 'libs/utils';
import { CWindowWidth } from 'constants/base.const';
import { CMentoringFieldType } from 'constants/mentoring.const';
import { RegionTypes, EmployedTypes } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Button, Input, Form, Checkbox, Tabs } from 'antd';
import { Colors } from 'GlobalStyles';
import { FormWrapper } from 'GlobalStyles';
import MainBackImage from 'assets/images/banners/4.webp';

const { TabPane } = Tabs;

const MentoringQNAWrite = () => {
  const location = useLocation();
  const { id } = getUrlQuery(location.search);
  const history = useHistory();
  const [title, setTitle] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [form] = Form.useForm();
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>();
  const [subcategories, setSubcategories] = useState<Array<{
    id: number;
    name: string;
  }> | null>();
  const [selectedCategories, setSelectedCategories] = useState<Array<number>>([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState<Array<number>>([]);
  const [specId, setSpecId] = useState<number>();
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);

  //초기 글쓰기 일 때  ==> 카테고리 O 서브 카테고리 X setState 해주기(서브 카테고리는 클릭된 메인 카테고리 바뀔 때 불러준다)
  const getCategories = async () => {
    const { success, data } = await getQNACategory();
    if (success) {
      setCategories(data);

      const spec = data?.filter((el: { id: number; name: string }) => el.name === '스펙');
      spec && setSpecId(spec[0].id);
    }
  };

  //수정 글 쓰기 일 때
  const getQNA = async () => {
    //초기 작성이 아닌 수정일 때
    const { success, data } = await getQNAItem(id);
    if (success) {
      const alreadySelectedCategory = data.categories.map((el: { id: number; name: string }) => el.id);
      const alreadySelectedSubcategory = data.sub_categories.map((el: { id: number; name: string }) => el.id);
      setTitle(data.title);
      setBody(data.body);
      setSelectedCategories(alreadySelectedCategory);
      setSelectedSubcategories(alreadySelectedSubcategory);
      form.setFieldsValue({
        category: [...alreadySelectedCategory],
      });

      if (alreadySelectedCategory.includes(specId)) {
        getSubcategory();
        form.setFieldsValue({
          spec_subcategory: [...alreadySelectedSubcategory],
        });
      }
    }
  };

  const getSubcategory = async () => {
    //서브 카테고리 get
    const { success, data } = await getQNASubcategory();
    if (success) setSubcategories(data);
  };

  // 스펙을 클릭 했을 때 선택된 카테고리 중에 '스펙'인지 아닌지 판별해서 서브 카테고리 세팅
  // 스펙을 선택 해제했을 경우에는 스펙의 서브카테고리 데이터도 리셋
  const handleClickedCategory = async () => {
    //클릭할 때마다 selectedCategories 상태 업데이트
    const clickedCategory = form.getFieldValue('category');
    const clickedSubcategory = form.getFieldValue('spec_subcategory');

    //클릭한 카테고리 관리
    if (clickedCategory) {
      //클릭한 메인 카테고리
      setSelectedCategories(clickedCategory);
    }

    if (clickedCategory.includes(specId)) {
      //스펙의 서브 카테고리 get
      getSubcategory();
      //스펙이 클릭되어있을 때 스펙 서브카테고리 클릭한 걸로 상태 업데이트
      setSelectedSubcategories(clickedSubcategory);
    } else {
      //서브 카테고리 데이터 리셋
      setSubcategories(null);
      //클릭한 서브 카테고리 리셋
      setSelectedSubcategories([]);
      //form도 category 는 유지 시키고 spec_subcategory만 리셋
      form.setFieldsValue({
        category: [...clickedCategory],
        spec_subcategory: [],
      });
    }
  };

  //글 작성하고 저장할 때 유효성 검사
  const checkForm = () => {
    //카테고리 선택 안했으면 알림
    if (selectedCategories.length < 1) {
      showAlert(undefined, '카테고리 선택', '질문의 카테고리를 선택해주세요.');
      return false;
    }

    //스펙 선택했는데 서브 카테고리 선택 안했으면 알림
    if (specId && selectedCategories.includes(specId) && selectedSubcategories.length < 1) {
      showAlert(undefined, '소주제 선택', '소주제를 선택해주세요.');
      return false;
    }

    const trimedTitle = trim(title);
    if (!trimedTitle) {
      showAlert(undefined, 'Q&A 작성하기', '제목을 입력해주세요.');
      return false;
    }
    const trimedBody = trim(body);
    if (!trimedBody) {
      showAlert(undefined, 'Q&A 작성하기', '내용을 입력해주세요.');
      return false;
    }

    return {
      title: trimedTitle,
      body: trimedBody,
      category_ids: selectedCategories,
      sub_category_ids: selectedSubcategories,
      // file_ids: files,
      mentoring_field: currentUser?.primary_field,
    };
  };

  const save = async () => {
    const params = checkForm();
    if (params) {
      //id가 있으면(글 수정=> put) id가 없으면 (초기 글쓰기 => post)
      const { success } = await saveQNA(params, id ? Number(id) : undefined, currentUser?.primary_field);
      if (success) history.push(ERoute.MentoringQNA);
    }
  };

  useEffect(() => {
    getCategories();

    //수정하기일 때는 이전 데이터 불러오기
    if (id) {
      getQNA();
    }
  }, [specId]);

  return (
    <>
      <Helmet>
        <title>{currentUser?.primary_field && CMentoringFieldType[currentUser?.primary_field]} - Q&A</title>
      </Helmet>
      <Grid image={MainBackImage} imageSize='cover' height='300px' md={{ height: '250px' }}>
        <Grid height='100%' backgroundColor={`${Colors.black}33`} justify='center'>
          <Grid width={`${CWindowWidth}px`} padding='0 7%' align='center' md={{ padding: '0 2%' }}>
            <Grid>
              <Text color={Colors.white} size='50px' bold>
                Q&A
              </Text>
            </Grid>
            {currentUser?.primary_field === 'consulting' ? (
              <Grid marginTop='5px'>
                <Text color={Colors.white} size='16px' md={{ size: '12px' }}>
                  컨설팅 취업준비와 관련된 무엇이든 물어보세요. Career Step 멘토가 답해드립니다.
                </Text>
              </Grid>
            ) : (
              <Grid marginTop='5px'>
                <Text color={Colors.white} size='16px' md={{ size: '12px' }}>
                  취업준비와 관련된 무엇이든 물어보세요. Career Step 멘토가 답해드립니다.
                </Text>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Tabs centered={true}>
        {currentUser?.primary_field && (
          <TabPane tab={`${toUpperCase(currentUser?.primary_field)} Q&A`} key={ERoute.MentoringQNA}></TabPane>
        )}
      </Tabs>

      <Grid justify='center'>
        <Grid width={`${CWindowWidth}px`} lg={{ width: '100%' }} padding='38px 20px 150px 20px'>
          <Grid>
            <Text size='2rem'>질문 작성하기</Text>
          </Grid>

          {!id && (
            <FormWrapper style={{ padding: '0px', margin: '30px 0 0 0' }}>
              <Form form={form} colon={false} layout='horizontal' className='form-normal'>
                <Form.Item name={'category'} label='카테고리'>
                  <Checkbox.Group className='checkbox-btn' onChange={handleClickedCategory}>
                    {categories?.map((category: { id: number; name: string }) => (
                      <Checkbox key={category.id} value={category.id}>
                        #{category.name}
                      </Checkbox>
                    ))}
                  </Checkbox.Group>
                </Form.Item>
                {subcategories && (
                  <Form.Item name={'spec_subcategory'} label='소주제'>
                    <Checkbox.Group className='checkbox-btn' onChange={handleClickedCategory}>
                      {subcategories.map((subcategory: { id: number; name: string }) => (
                        <Checkbox key={subcategory.id} value={subcategory.id}>
                          #{subcategory.name}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                )}
              </Form>
            </FormWrapper>
          )}

          {id && (
            <FormWrapper style={{ padding: '0px', margin: '30px 0 0 0' }}>
              <Form form={form} colon={false} layout='horizontal' className='form-normal'>
                {selectedCategories && (
                  <Form.Item
                    name={'category'}
                    //옆에 뜨는 라벨 네임
                    label='카테고리'
                  >
                    <Checkbox.Group className='checkbox-btn' onChange={handleClickedCategory}>
                      {categories?.map((category: { id: number; name: string }) => (
                        <Checkbox key={category.id} value={category.id}>
                          #{category.name}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                )}
                {subcategories && (
                  <Form.Item name={'spec_subcategory'} label='소주제'>
                    <Checkbox.Group className='checkbox-btn' onChange={handleClickedCategory}>
                      {subcategories.map((subcategory: { id: number; name: string }) => (
                        <Checkbox key={subcategory.id} value={subcategory.id}>
                          #{subcategory.name}
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </Form.Item>
                )}
              </Form>
            </FormWrapper>
          )}

          <Grid marginTop='50px'>
            <Text size='1.1rem'>제목</Text>
          </Grid>
          <Grid marginTop='5px'>
            <Input
              size='large'
              placeholder='제목을 입력해주세요'
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid marginTop='25px'>
            <EditorComponent content={body} onChange={setBody} apiPath='/library/image/' />
          </Grid>

          <Grid justify='flex-end' marginTop='50px'>
            <Grid isInline gap='20px'>
              <Button size='large' onClick={() => history.push(ERoute.MentoringQNA)}>
                취소
              </Button>
              <Button type='primary' size='large' onClick={save}>
                {id ? '수정' : '등록'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MentoringQNAWrite;
